import React from 'react'
import styled, { css, keyframes } from 'styled-components'
import { node, bool, string } from 'prop-types'
import { mix, readableColor, stripUnit } from 'polished'
import { Link } from 'gatsby'

import { red, white, black } from 'styles/colors'
import { animationTime, animationCurve, inputHeight, inputHeightMobile } from 'styles/global'
import { fontWeights, fontSizes } from 'styles/typography'
import * as spacing from 'styles/spacing'
import { media } from 'styles/media'

import SpinnerIcon from 'icons/spinner.svg'

import Icon from 'components/Icon'

export const rotate = keyframes`
  100% {
    transform: rotate(360deg);
  }
`

export const Container = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: ${inputHeightMobile};
  border-radius: ${spacing.xLarge};
  padding: 0 ${spacing.large};
  cursor: pointer;
  text-align: center;
  transition: all ${animationTime} ${animationCurve};

  ${media.medium`
    height: ${inputHeight};
  `};

  ${media.large`
    padding: 0 ${spacing.xLarge};
  `};

  ${props =>
    props.fullwidth &&
    css`
      display: flex;
      width: 100%;
    `}

  ${props =>
    props.theme === 'primary' &&
    css`
      color: ${white};
      background-color: ${black};

      &:hover,
      &:focus,
      &:active {
        background-color: ${mix(0.93, black, black)};
      }
    `}

  ${props =>
    props.theme === 'secondary' &&
    css`
      color: ${black};
      background-color: transparent;
      border: 1px solid ${black};

      &:hover,
      &:focus,
      &:active {
        background-color: ${black};
        color: ${white};
      }
    `}

  ${props =>
    props.theme === 'tertiary' &&
    css`
      color: ${white};
      background-color: transparent;
      border: 2px solid ${white};

      &:hover,
      &:focus,
      &:active {
        background-color: ${white};
        color: ${black};
      }
    `}

  ${props =>
    props.theme === 'negative' &&
    css`
      color: ${white};
      background-color: ${red};

      &:hover,
      &:focus,
      &:active {
        background-color: ${mix(0.93, red, black)};
      }
    `}

  ${props =>
    props.theme === 'custom' &&
    css`
      color: ${readableColor(props.customColor)};
      background-color: ${props.customColor};

      &:hover,
      &:focus,
      &:active {
        background-color: ${mix(0.93, props.customColor, black)};
      }
    `}

  ${props =>
    props.theme === 'custom' &&
    props.variant === 'outline' &&
    css`
      color: ${props.customColor};
      background-color: transparent;
      border: 2px solid ${props.customColor};

      &:hover,
      &:focus,
      &:active {
        background-color: ${props.customColor};
        color: ${readableColor(props.customColor)};
      }
    `}

  ${props =>
    props.variant === 'square' &&
    css`
      border-radius: 0 !important;
    `}

  ${props =>
    props.size === 'large' &&
    css`
      height: ${stripUnit(inputHeightMobile) * 1.3 + 'px'};
      padding: 0 ${spacing.xxLarge};
      text-transform: uppercase;
      border-width: 1px;

      em {
        font-style: normal;
        text-transform: lowercase;
      }

      ${media.medium`
        height: ${stripUnit(inputHeight) * 1.4 + 'px'};
        padding: 0 ${spacing.xxxLarge};
        font-size: 1.25rem;
      `};
    `}

  ${props =>
    props.size === 'small' &&
    css`
      height: ${stripUnit(inputHeightMobile) * 0.7 + 'px'};
      padding: 0 ${stripUnit(spacing.small) * 1.5 + 'px'};

      ${media.medium`
        height: ${stripUnit(inputHeight) * 0.7 + 'px'};
        padding: 0 ${spacing.medium};
      `};
    `}

  &[disabled] {
    cursor: default;
    pointer-events: none;
    opacity: 0.4;
    user-select: none;
    cursor: not-allowed;
  }
`

export const Spinner = styled.div`
  display: flex;
  position: absolute;
  top: 50%;
  left: 50%;
  margin-left: -12px;
  margin-top: -12px;
  animation: ${rotate} 0.75s linear infinite;
`

const Label = styled.span`
  font-weight: ${fontWeights.bold};
  transition: opacity ${animationTime} ${animationCurve};
  font-size: ${fontSizes.small};

  ${props =>
    props.size === 'large' &&
    css`
      font-size: 1rem;
      font-weight: ${fontWeights.regular};
      letter-spacing: 0.1em;
    `}

  ${props =>
    props.size === 'small' &&
    css`
      font-size: ${fontSizes.tiny};
    `}

  ${props =>
    props.isLoading &&
    css`
      opacity: 0;
    `};
`

const getElement = props => {
  const link = props.to || props.href

  if (link) {
    if (link.startsWith('http')) return 'a'
    return Link
  }

  return 'button'
}

const Button = ({ children, theme, customColor, variant, size, isLoading, fullwidth, type = 'button', ...rest }) => (
  <Container
    type={type}
    fullwidth={fullwidth}
    theme={theme}
    variant={variant}
    size={size}
    isLoading={isLoading}
    disabled={isLoading}
    customColor={customColor}
    as={getElement(rest)}
    {...rest}
  >
    {isLoading && (
      <Spinner>
        <Icon icon={<SpinnerIcon />} />
      </Spinner>
    )}
    <Label isLoading={isLoading} size={size}>
      {children}
    </Label>
  </Container>
)

Button.propTypes = {
  children: node.isRequired,
  theme: string,
  variant: string,
  size: string,
  customColor: string,
  fullwidth: bool,
  isLoading: bool,
  type: string
}

export default Button
